/** Zone-name fixes from the portable name-fix page. Conditioned zones only. */

export type NamingMode = 'floor' | 'unit';
export type ModelKind = 'idf' | 'json';

export type ParsedZone = {
  name: string;
  partOfTotal: boolean;
};

export type DupParsed = {
  floor: string;
  unit: string;
  room: string;
};

export type ExtraXFix = {
  fixed: string;
  removeSegs: string[];
  beforeX: number;
  afterX: number;
  needsFix: boolean;
};

export type MissingXFix = {
  needsFix: boolean;
  fixed: string;
  kind: '' | 'num-first' | 'name-first' | 'append-x1';
};

export type XAroundItem = {
  name: string;
  fixed: string;
  removeSegs: string[];
  kind: 'extra' | 'missing';
};

export type DupRow = {
  name: string;
  unit: string;
  parsed: DupParsed;
};

/**
 * Same unit token on both sides of ":".
 * 04X48:48XLIVING and 04X1V15:1V15XLIVING.
 * The token must match in full, letters included, so 1115 is not a copy of 15.
 */
const DUP_RE = /^(\d+)X([0-9A-Za-z]+):(\2)X(.+)$/i;

/** Letters and digits, excluding the separator X. */
const TOKEN = '[0-9A-WYZ]';
const UNIT_NAME_RE = new RegExp(
  `^(${TOKEN}+)X(${TOKEN}+)(?:X(?:NW|NE|SE|SW|N|S|E|W))?:(${TOKEN}+)$`
);
const FLOOR_NAME_RE = new RegExp(`^(${TOKEN}+):(${TOKEN}+)X(${TOKEN}+)$`);

export const isValidUnitName = (name: string): boolean =>
  UNIT_NAME_RE.test(String(name || '').toUpperCase());

export const isValidFloorName = (name: string): boolean =>
  FLOOR_NAME_RE.test(String(name || '').toUpperCase());

export const isYes = (v: unknown): boolean => {
  const s = String(v == null ? '' : v).trim().toLowerCase();
  return s === 'yes' || s === '1' || s === 'true';
};

const stripIdfComments = (line: string) => {
  const i = line.indexOf('!');
  return i >= 0 ? line.slice(0, i) : line;
};

export const parseIdfZones = (text: string): ParsedZone[] => {
  const zones: ParsedZone[] = [];
  const lines = String(text).split(/\r?\n/);
  let i = 0;
  while (i < lines.length) {
    const raw = stripIdfComments(lines[i]).trim();
    if (!/^Zone\s*,/i.test(raw)) {
      i += 1;
      continue;
    }
    let chunk = raw;
    while (chunk.indexOf(';') < 0 && i + 1 < lines.length) {
      i += 1;
      chunk += `\n${stripIdfComments(lines[i])}`;
    }
    const body = chunk.replace(/^Zone\s*,/i, '').replace(/;\s*$/, '');
    const fields = body.split(',').map((f) => f.trim());
    zones.push({
      name: fields[0] || '',
      partOfTotal: isYes(fields.length ? fields[fields.length - 1] : ''),
    });
    i += 1;
  }
  return zones;
};

export const parseJsonZones = (text: string): ParsedZone[] => {
  const data = JSON.parse(text) as { Zone?: Record<string, { part_of_total_floor_area?: unknown }>; zone?: Record<string, { part_of_total_floor_area?: unknown }> };
  const zoneObj = data.Zone || data.zone || {};
  return Object.keys(zoneObj).map((name) => {
    const z = zoneObj[name] || {};
    return {
      name,
      partOfTotal: isYes(z.part_of_total_floor_area),
    };
  });
};

export const detectKind = (name: string, text: string): ModelKind => {
  const n = String(name || '').toLowerCase();
  if (/\.idf$/i.test(n)) return 'idf';
  if (/\.(epjson|json)$/i.test(n)) return 'json';
  return String(text || '').trim().charAt(0) === '{' ? 'json' : 'idf';
};

export const parseDup = (name: string): DupParsed | null => {
  const m = DUP_RE.exec(name);
  if (!m) return null;
  return { floor: m[1], unit: m[2], room: m[4] };
};

const withoutX = (value: string) => String(value || '').replace(/X/gi, '');

export const fixedName = (parsed: DupParsed | null, mode: NamingMode): string | null => {
  if (!parsed) return null;
  if (mode === 'unit') return `${parsed.floor}X${parsed.unit}:${parsed.room}`;
  // 00X50:50XMAMADX1 → 00:50XMAMAD1. The X inside the zone name is dropped.
  const room = withoutX(parsed.room);
  if (!room) return null;
  const fixed = `${parsed.floor}:${parsed.unit}X${room}`;
  return isValidFloorName(fixed) ? fixed : null;
};

const countX = (s: string) => (String(s).match(/X/gi) || []).length;

const ORIENTATION_RE = /^(?:NW|NE|SE|SW|N|S|E|W)$/i;
const ALNUM_RE = /^[0-9A-Za-z]+$/;
const splitX = (value: string) => String(value || '').split(/X/i);

/**
 * Drop extra X segments on the left. A real orientation stays.
 * 01X2X3 → 01X2. 01X2X3XSW → 01X2XSW. 01X2XSW stays.
 */
const repairUnitBefore = (before: string): string | null => {
  const parts = splitX(before);
  if (parts.length < 2 || parts.some((part) => !ALNUM_RE.test(part))) return null;
  const floor = parts[0];
  const apt = parts[1];
  const extras = parts.slice(2);
  if (extras.length === 0) return before;
  const orientation = extras.filter((token) => ORIENTATION_RE.test(token)).pop();
  if (extras.length === 1 && orientation) return before;
  if (orientation) return `${floor}X${apt}X${orientation}`;
  return `${floor}X${apt}`;
};

const droppedXSegments = (before: string, fixedBefore: string): string[] => {
  const original = splitX(before);
  const kept = splitX(fixedBefore);
  const dropped: string[] = [];
  let keptIndex = 0;
  original.forEach((part, index) => {
    if (keptIndex < kept.length && part === kept[keptIndex]) {
      keptIndex += 1;
      return;
    }
    if (index > 0) dropped.push(`X${part}`);
  });
  return dropped;
};

const emptyExtraFix = (name: string): ExtraXFix => ({
  fixed: name,
  removeSegs: [],
  beforeX: 0,
  afterX: 0,
  needsFix: false,
});

/**
 * A second X before ":" is removed unless it is an orientation.
 * 00X48X1:2XLIVING → 00X48:2XLIVING. 00X1XSW:LIVING stays.
 */
/**
 * Block per floor: drop every X after the apartment separator and keep the text.
 * 00:50XMAMADX1 → 00:50XMAMAD1.
 */
const fixFloorZoneX = (name: string): { fixed: string; removeSegs: string[] } | null => {
  const colon = String(name).indexOf(':');
  if (colon < 0) return null;
  const before = name.slice(0, colon);
  const after = name.slice(colon + 1);
  if (/X/i.test(before)) return null;
  const sep = after.search(/X/i);
  if (sep < 0) return null;
  const zoneRaw = after.slice(sep + 1);
  if (!/X/i.test(zoneRaw)) return null;
  const zone = withoutX(zoneRaw);
  const fixed = `${before}:${after.slice(0, sep)}X${zone}`;
  if (!isValidFloorName(fixed)) return null;
  return {
    fixed,
    removeSegs: (zoneRaw.match(/X/gi) || []).map(() => 'X'),
  };
};

export const fixExtraXName = (name: string, mode: NamingMode = 'unit'): ExtraXFix => {
  if (String(name).indexOf(':') < 0) return emptyExtraFix(name);
  if (isValidUnitName(name) || isValidFloorName(name)) return emptyExtraFix(name);
  if (mode === 'floor') {
    const floorFix = fixFloorZoneX(name);
    if (floorFix) {
      return {
        ...emptyExtraFix(floorFix.fixed),
        fixed: floorFix.fixed,
        removeSegs: floorFix.removeSegs,
        needsFix: true,
      };
    }
  }
  const colon = name.indexOf(':');
  const before = name.slice(0, colon);
  const after = name.slice(colon + 1);
  const beforeX0 = countX(before);
  const afterX0 = countX(after);
  const newBefore = repairUnitBefore(before);
  if (!newBefore || newBefore === before) {
    return { ...emptyExtraFix(name), beforeX: beforeX0, afterX: afterX0 };
  }
  const fixed = `${newBefore}:${after}`;
  // A second X before ":" is removed even when the zone still contains one.
  // 00X48X1:2XLIVING → 00X48:2XLIVING. An orientation X stays.
  return {
    fixed,
    removeSegs: droppedXSegments(before, newBefore),
    beforeX: beforeX0,
    afterX: afterX0,
    needsFix: true,
  };
};

/**
 * No X after ":" (block per floor):
 * 07:43LIVING → 07:43XLIVING
 * 05:MAMAD31 → 05:31XMAMAD
 * 04:CLUB → 04:CLUBX1
 *
 * A letter-only room has no apartment number, so X1 is appended.
 * Unit block already keeps the separator before ":". A room with no X
 * is complete, including a trailing number: 12XPH6F:TOILETS1 stays.
 */
export const fixMissingXAfterName = (name: string, mode: NamingMode = 'floor'): MissingXFix => {
  const colon = String(name).indexOf(':');
  if (colon < 0) return { needsFix: false, fixed: name, kind: '' };
  const before = name.slice(0, colon);
  const after = name.slice(colon + 1);
  if (mode === 'unit' && /X/i.test(before) && !/X/i.test(after)) {
    return { needsFix: false, fixed: name, kind: '' };
  }
  if (/X/i.test(after)) return { needsFix: false, fixed: name, kind: '' };
  if (!/\d/.test(after)) {
    if (mode !== 'floor' || /X/i.test(before)) {
      return { needsFix: false, fixed: name, kind: '' };
    }
    const candidate = `${before}:${after}X1`;
    if (!isValidFloorName(candidate)) return { needsFix: false, fixed: name, kind: '' };
    return { needsFix: true, fixed: candidate, kind: 'append-x1' };
  }

  const numFirst = after.match(/^(\d+)([A-Za-z].*)$/);
  const nameFirst = after.match(/^([A-Za-z]+)(\d+)$/);
  const candidate = numFirst
    ? `${before}:${numFirst[1]}X${numFirst[2]}`
    : nameFirst
      ? `${before}:${nameFirst[2]}X${nameFirst[1]}`
      : '';
  const kind: MissingXFix['kind'] = numFirst ? 'num-first' : nameFirst ? 'name-first' : '';
  if (!candidate) return { needsFix: false, fixed: name, kind: '' };
  const legal =
    mode === 'floor'
      ? isValidFloorName(candidate)
      : isValidUnitName(candidate) || isValidFloorName(candidate);
  if (!legal) return { needsFix: false, fixed: name, kind: '' };
  return { needsFix: true, fixed: candidate, kind };
};

export const conditionedDups = (zones: ParsedZone[]): DupRow[] =>
  zones
    .filter((z) => z.partOfTotal && parseDup(z.name))
    .map((z) => {
      const parsed = parseDup(z.name) as DupParsed;
      return { name: z.name, unit: parsed.unit, parsed };
    });

export const collectXAround = (zones: ParsedZone[], mode: NamingMode = 'floor'): XAroundItem[] => {
  const byName: Record<string, XAroundItem> = {};
  zones
    .filter((z) => z.partOfTotal)
    .forEach((z) => {
      const extra = fixExtraXName(z.name, mode);
      if (!extra.needsFix) return;
      byName[z.name] = {
        name: z.name,
        fixed: extra.fixed,
        removeSegs: extra.removeSegs,
        kind: 'extra',
      };
    });
  zones
    .filter((z) => z.partOfTotal)
    .forEach((z) => {
      if (byName[z.name]) return;
      const missing = fixMissingXAfterName(z.name, mode);
      if (!missing.needsFix) return;
      byName[z.name] = {
        name: z.name,
        fixed: missing.fixed,
        removeSegs: [],
        kind: 'missing',
      };
    });
  return Object.keys(byName)
    .sort()
    .map((k) => byName[k]);
};

export const buildDupRenameMap = (zones: ParsedZone[], mode: NamingMode): Record<string, string> => {
  const map: Record<string, string> = {};
  conditionedDups(zones).forEach((z) => {
    const neu = fixedName(z.parsed, mode);
    if (neu && neu !== z.name) map[z.name] = neu;
  });
  return map;
};

export const applyRenamesToText = (text: string, map: Record<string, string>): string => {
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);
  let out = text;
  keys.forEach((oldName) => {
    out = out.split(oldName).join(map[oldName]);
  });
  return out;
};

export const applyRenamesToJson = (text: string, map: Record<string, string>): string => {
  const data = JSON.parse(text);
  const keys = Object.keys(map).sort((a, b) => b.length - a.length);

  const walk = (node: unknown): unknown => {
    if (typeof node === 'string') {
      let s = node;
      keys.forEach((oldName) => {
        if (s.indexOf(oldName) >= 0) s = s.split(oldName).join(map[oldName]);
      });
      return s;
    }
    if (Array.isArray(node)) return node.map(walk);
    if (node && typeof node === 'object') {
      const out: Record<string, unknown> = {};
      Object.keys(node as Record<string, unknown>).forEach((k) => {
        out[map[k] || k] = walk((node as Record<string, unknown>)[k]);
      });
      return out;
    }
    return node;
  };

  return JSON.stringify(walk(data), null, 4);
};

export const applyRenames = (text: string, kind: ModelKind, map: Record<string, string>): string =>
  kind === 'json' ? applyRenamesToJson(text, map) : applyRenamesToText(text, map);

export const renameZones = (zones: ParsedZone[], map: Record<string, string>): ParsedZone[] =>
  zones.map((z) => (map[z.name] ? { ...z, name: map[z.name] } : z));

export const outputFileName = (fileName: string, kind: ModelKind): string => {
  const base = fileName.replace(/\.(idf|epjson|json)$/i, '');
  const ext = kind === 'json' ? (/\.epjson$/i.test(fileName) ? '.epJSON' : '.json') : '.idf';
  return `${base}_zonesFixed${ext}`;
};

export type NamePart = { text: string; highlight: boolean };

export const highlightParts = (name: string, needles: string[]): NamePart[] => {
  let parts: NamePart[] = [{ text: name, highlight: false }];
  const sorted = needles.filter(Boolean).slice().sort((a, b) => b.length - a.length);
  sorted.forEach((seg) => {
    const next: NamePart[] = [];
    parts.forEach((part) => {
      if (part.highlight || !part.text) {
        next.push(part);
        return;
      }
      let rest = part.text;
      while (rest.length) {
        const i = rest.indexOf(seg);
        if (i < 0) {
          next.push({ text: rest, highlight: false });
          break;
        }
        if (i > 0) next.push({ text: rest.slice(0, i), highlight: false });
        next.push({ text: seg, highlight: true });
        rest = rest.slice(i + seg.length);
      }
    });
    parts = next;
  });
  return parts;
};
